import { useEffect, useState } from "react";
import { useDropzone } from "react-dropzone";
import { FiCheck, FiFile, FiImage, FiUploadCloud, FiX } from "react-icons/fi";
import "./fileField.css";

const toAccept = (accept) => {
	if (!accept) return undefined;
	if (typeof accept === "object") return accept;
	return accept.split(",").reduce((result, part) => {
		const value = part.trim();
		if (value.startsWith(".")) (result["application/x-akello-file"] || (result["application/x-akello-file"] = [])).push(value);
		else if (value) result[value] = [];
		return result;
	}, {});
};
const fileSize = (size) => size < 1024 * 1024 ? `${Math.max(1, Math.round(size / 1024))} KB` : `${(size / 1024 / 1024).toFixed(1)} MB`;

export default function FileField({ id, name, label = "File", file, accept, maxSize, hint, disabled,
	required, invalid, describedBy, existing = false, onSelect }) {
	const [rejection, setRejection] = useState("");
	const [preview, setPreview] = useState(null);
	const selected = file && typeof file.name === "string" ? file : null;
	useEffect(() => {
		if (!selected?.type?.startsWith("image/") || !URL.createObjectURL) { setPreview(null); return; }
		const url = URL.createObjectURL(selected);
		setPreview(url);
		return () => URL.revokeObjectURL(url);
	}, [selected]);
	const { getRootProps, getInputProps, open, isDragActive, isDragReject } = useDropzone({
		accept: toAccept(accept), maxSize, multiple: false, disabled, noClick: true, noKeyboard: true,
		onDropAccepted: ([next]) => { setRejection(""); onSelect(next); },
		onDropRejected: (files) => setRejection(files.length > 1 ? "Choose one file at a time." : files[0]?.errors?.some(({ code }) => code === "file-too-large")
			? `This file is too large.${maxSize ? ` Maximum size: ${fileSize(maxSize)}.` : " Choose a smaller file."}` : "This file type isn’t supported. Choose an accepted format."),
	});
	return <div className={`akello-file-field${disabled ? " is-disabled" : ""}`}>
		<div {...getRootProps({ className: `akello-file-drop${isDragActive ? " is-dragging" : ""}${isDragReject || invalid || rejection ? " is-invalid" : ""}` })}>
			<input {...getInputProps({ id, name, "aria-label": label, "aria-invalid": !!(invalid || rejection), "aria-describedby": [describedBy, rejection && `${id}-rejection`].filter(Boolean).join(" ") || undefined, required: required && !selected && !existing })} />
			<span className="akello-file-upload-icon" aria-hidden="true"><FiUploadCloud /></span>
			<strong>{isDragActive ? "Drop your file here" : selected || existing ? "Choose a replacement" : "Drag & drop your file here"}</strong>
			<span className="akello-file-hint">{hint || "Or browse files on your device"}</span>
			<button type="button" className="akello-file-browse" onClick={open} disabled={disabled}>{selected || existing ? "Replace file" : "Browse files"}</button>
		</div>
		{(selected || existing) && <div className="akello-file-selection" role="status">
			<span className="akello-file-thumbnail" aria-hidden="true">{preview ? <img src={preview} alt="" /> : selected?.type?.startsWith("image/") ? <FiImage /> : <FiFile />}</span>
			<div className="akello-file-details"><strong>{selected?.name || "File already uploaded"}</strong>
				<span><FiCheck aria-hidden="true" />{selected ? `${fileSize(selected.size)} · Ready to upload` : "Saved to this book"}</span></div>
			{selected && <button type="button" className="akello-file-remove" aria-label={`Remove ${selected.name}`} disabled={disabled} onClick={() => { setRejection(""); onSelect(null); }}><FiX aria-hidden="true" /></button>}
		</div>}
		{rejection && <p id={`${id}-rejection`} className="akello-field-error" role="alert">{rejection}</p>}
	</div>;
}
